import React, { useState, useEffect } from "react";
import { useOrderChangesDialog } from "../../../Components/orders/useOrderChangesDialog";
import { fetchWaitingConfirmingOrders, updateOrderStatus, deleteOrder, checkDuplicatePhoneCount } from "../../../api/OrderAPIs";
import { fetchDesignById } from "../../../api/DesignAPIs";
import { fetchTypeById } from "../../../api/TypeAPIs";
import { readyOrderUnits,formatPhoneWithCaseStyle, teePlusPackedItemData } from "../../../utils/orderLineDisplay";
import { copyOrderToTeePlus } from "../../../utils/productionSource";
import { useAuth } from "../../../utils/auth";
import { canViewOrders, canMutateOrders, isAdmin } from "../../../utils/roles";
import { useNavigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { API_BASE_URL_V2 } from "../../../api/URL_Header";
import { downloadCustomFile } from "../../../api/FileAPIs";
import {
  ChevronRight,
  Package,
  User,
  Phone,
  MapPin,
  Map,
  DollarSign,
  Hash,
  Copy,
  Download,
  Link as LinkIcon,
  Clock,
  CheckCircle,
  AlertTriangle,
  Trash2,
  Archive,
  Loader,
  ChevronUp,
  Search,
  Filter,
  CopyCheck,
  Save,
  Plus,
  Minus,
  Truck,
  MessageSquare,
  XCircle,
  Edit,
  ShoppingCart,
  ChevronLeft,
  FileText,
} from "lucide-react";

const SkeletonLoader = ({ type }) => {
  if (type === "table") {
    return (
      <div className="animate-pulse">
        <div className="h-8 bg-gray-200 rounded mb-4"></div>
        {[...Array(3)].map((_, index) => (
          <div key={index} className="flex gap-4 border-b py-3">
            <div className="w-12 h-12 bg-gray-200 rounded"></div>
            <div className="flex-1 space-y-2">
              <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/2"></div>
            </div>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const WaitingConfirmingOrders = () => {
  const navigate = useNavigate();
  const { authState } = useAuth();
  const canAct = canMutateOrders(authState.role);
  const { changesDialog, openOrderChanges } = useOrderChangesDialog(async (savedOrderId) => {
    setLoadedOrders((prev) => prev.filter((o) => o.orderNumber !== savedOrderId));
    toast.success("تم تحديث الطلب إلى Changes بنجاح");
  });
  
  const [loadedOrders, setLoadedOrders] = useState([]);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isOrdersLoading, setIsOrdersLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [pageSize, setPageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [currentReference, setCurrentReference] = useState("Girabatcom.16000");
  const [referenceInput, setReferenceInput] = useState("Girabatcom.16000");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!canViewOrders(authState.role)) {
      navigate("/login");
      return;
    }
    loadInitialOrders();
  }, [authState, navigate]);

  useEffect(() => {
    const saved = localStorage.getItem("orderReference");
    if (saved) {
      setCurrentReference(saved);
      setReferenceInput(saved);
    }
  }, []);

  // ─── Copy helpers ──────────────────────────────────────────────────────────

  const copyToClipboard = (text) => {
    if (navigator.clipboard && window.isSecureContext) {
      return navigator.clipboard.writeText(text).then(() => {}).catch(() => fallbackCopy(text));
    } else {
      fallbackCopy(text);
    }
  };

  const fallbackCopy = (text) => {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.opacity = "0";
    textArea.style.left = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();
    try {
      const successful = document.execCommand("copy");
      if (!successful) toast.error("فشل النسخ، حاول مرة أخرى.");
    } catch (err) {
      toast.error("فشل النسخ، حاول النسخ يدويًا.");
    }
    document.body.removeChild(textArea);
  };

  // ─── Download ──────────────────────────────────────────────────────────────

  const handleDownload = async (url, filename = "design.jpg") => {
    try {
      await downloadCustomFile(url, filename);
    } catch (err) {
      console.error("Download failed:", err);
      toast.error("فشل التحميل. تحقق من السيرفر.");
    }
  };

  // ─── Enrich orders ─────────────────────────────────────────────────────────

  const enrichOrders = async (data) => {
    return await Promise.all(
      data.map(async (order) => {
        let enrichedItems = [];
        for (const detail of order.orderDetails) {
          const readyUnits = await readyOrderUnits(detail);
          if (readyUnits) {
            enrichedItems.push(...readyUnits);
            continue;
          }
          const design = await fetchDesignById(detail.designId);
          const type = await fetchTypeById(detail.typeId);
          if (!design || !type) continue;
          const baseItem = {
            id: detail.designId,
            name: design.Name,
            price: detail.price,
            type: formatPhoneWithCaseStyle(type.name, detail),
            driveLink: design.code.startsWith("CUSTOM")
              ? `${API_BASE_URL_V2}${design.driveLink}`
              : design.driveLink || "",
            code: design.code || "None",
          };
          for (let i = 0; i < detail.quantity; i++) {
            enrichedItems.push({ ...baseItem, quantityIndex: i + 1, totalQuantity: detail.quantity });
          }
        }
        return {
          orderNumber: order.orderId,
          items: enrichedItems,
          total: order.totalAmount,
          profit: order.profit || 0,
          date: new Date(order.date).toISOString().split("T")[0],
          address: order.address?.fullAddress || "Unknown Address",
          state: order.address?.state || "Unknown Address",
          phoneNumber: order.phoneNumber || "Unknown Phone",
          phoneNumber2: order.phoneNumber2 || "Unknown Phone",
          name: order.name || "Unknown Customer",
          note: order.note || "",
          changeNoteImageCount: Number(order.changeNoteImageCount || 0),
          status: order.status || "WaitingConfirming",
        };
      })
    );
  };

  // ─── Load orders ───────────────────────────────────────────────────────────

  const loadInitialOrders = async () => {
    setIsOrdersLoading(true);
    try {
      const data = await fetchWaitingConfirmingOrders(0, pageSize);
      if (data.length === 0 || data.length < pageSize) setHasMore(false);
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders(enrichedOrders);
      setCurrentPage(1);
      setTotalCount(enrichedOrders.length);
    } catch (err) {
      console.error("Error loading initial orders:", err);
      setError("فشل في تحميل الطلبات. يرجى المحاولة مرة أخرى.");
      toast.error("فشل في تحميل الطلبات!");
    } finally {
      setIsOrdersLoading(false);
    }
  };

  const loadMoreOrders = async () => {
    setIsLoadingMore(true);
    try {
      const data = await fetchWaitingConfirmingOrders(currentPage, pageSize);
      if (data.length === 0 || data.length < pageSize) setHasMore(false);
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders((prev) => [...prev, ...enrichedOrders]);
      setCurrentPage((prev) => prev + 1);
      setTotalCount((prev) => prev + enrichedOrders.length);
    } catch (err) {
      console.error("Error loading more orders:", err);
      toast.error("فشل في تحميل المزيد من الطلبات!");
    } finally {
      setIsLoadingMore(false);
    }
  };

  const handlePageSizeChange = async (newSize) => {
    setPageSize(newSize);
    setCurrentPage(0);
    setIsOrdersLoading(true);
    try {
      const data = await fetchWaitingConfirmingOrders(0, newSize);
      const enrichedOrders = await enrichOrders(data);
      setLoadedOrders(enrichedOrders);
      setCurrentPage(1);
      setHasMore(data.length === newSize);
      setTotalCount(enrichedOrders.length);
    } catch (err) {
      toast.error("فشل في تحميل الطلبات!");
    } finally {
      setIsOrdersLoading(false);
    }
  };

  // ─── Status update ─────────────────────────────────────────────────────────

  const handleUpdateStatus = async (orderId, newStatus) => {
    if (!canAct) return;
    let reason = "no reason (normal order)";

    if (newStatus === "Hold") {
      reason = window.prompt("سبب وضع الطلب على الانتظار:");
      if (reason === null) return;
      if (!reason.trim()) { toast.error("يجب إدخال سبب!"); return; }
    } else if (newStatus === "Changes") {
      openOrderChanges(orderId);
      return;
    } else if (newStatus === "Spam") {
      reason = window.prompt("سبب التغيير:");
      if (reason === null) return;
      if (!reason.trim()) { toast.error("يجب إدخال سبب!"); return; }
    }

    const confirm = window.confirm(`تأكيد تحديث الحالة إلى "${newStatus}"؟`);
    if (!confirm) return;

    try {
      await updateOrderStatus(orderId, newStatus, reason);
      setLoadedOrders((prev) => prev.filter((o) => o.orderNumber !== orderId));
      toast.success(`تم تحديث الطلب إلى ${newStatus} بنجاح`);
    } catch (err) {
      toast.error("فشل التحديث: " + err.message);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (!canAct) return;
    if (!canMutateOrders(authState.role)) return;
    const confirmDelete = window.confirm(`هل أنت متأكد من حذف الطلب رقم ${orderId}؟`);
    if (!confirmDelete) return;
    try {
      await deleteOrder(orderId);
      setLoadedOrders((prev) => prev.filter((order) => order.orderNumber !== orderId));
      toast.success(`تم حذف الطلب رقم ${orderId} بنجاح!`);
    } catch (err) {
      toast.error("فشل في حذف الطلب. يرجى المحاولة مرة أخرى.");
    }
  };

  // ─── Copy messages ─────────────────────────────────────────────────────────

  const handleCopyStockApology = () => {
    const message = `أهلاً بحضرتك 🌸
مع حضرتك فاطمة من جرابتكم ستور 💛

بنعتذر جداً عن التأخير في تنفيذ الطلب الفترة اللي فاتت،
كان فيه نقص في نوع الجراب المطلوب، لكن الحمد لله تم توفيره الآن وجاهز للتنفيذ ✅

هل نبدأ في تجهيز الطلب فوراً؟ 

نقدّر جداً صبرك وذوقك ❤️
فاطمة - فريق جرابتكم`;
    copyToClipboard(message);
    toast.success("تم نسخ رسالة الاعتذار!");
  };

  // ─── Copy single order (Excel format) ─────────────────────────────────────

  const isOrderNonCustom = (order) =>
    !order.items.some((item) => item.code && item.code.startsWith("CUSTOM"));

  const handleCopyAllExcelData = async (order) => {
    try {
      const count = await checkDuplicatePhoneCount(
        order.orderNumber,
        order.phoneNumber,
        order.phoneNumber2 || ""
      );
      if (count > 0) {
        const proceed = window.confirm(
          `تحذير: هذا العميل لديه ${count} طلب(ات) سابقة بنفس الرقم!\n\nهل تريد الاستمرار في النسخ؟`
        );
        if (!proceed) { toast.info("تم إلغاء النسخ بسبب تكرار الرقم"); return; }
      }

      const phone = `${order.phoneNumber}${
        order.phoneNumber2 && order.phoneNumber2 !== "Unknown Phone" ? "-" + order.phoneNumber2 : ""
      }`;
      const formattedState = `${order.state} (D)`;
      const customerBase = `${currentReference}\t\t${order.name}\t${phone}\t\t${order.address}\t${formattedState}\t${order.total}\tCOD\t`;

      const items = order.items;
      const lines = [];
      items.forEach((item, index) => {
        const itemNum = `${index + 1}-${items.length}`;
        const itemData = teePlusPackedItemData(item);
        const itemLine = `${itemNum}${itemData}`;
        if (index === 0) {
          lines.push(`${customerBase}\t\t${itemLine}`);
        } else {
          lines.push(`${currentReference}\t\t\t\t\t\t\t\t\t\t\t${itemLine}`);
        }
      });

      await copyOrderToTeePlus(lines.join("\n"), order.orderNumber);
      toast.success("تم نسخ الطلب كامل بنجاح!");
    } catch (err) {
      if (err.code === "SOURCE_FAILED") toast.warn("تم النسخ إلى TeePlus، لكن تعذر تحديث مصدر الإنتاج");
      else toast.error("فشل في النسخ، حاول مرة تانية");
    }
  };

  // ─── Copy all visible orders ───────────────────────────────────────────────

  const handleCopyAllVisibleOrders = async () => {
    const nonCustomOrders = loadedOrders.filter(isOrderNonCustom);
    if (nonCustomOrders.length === 0) {
      toast.warn("لا توجد طلبات غير مخصصة (CUSTOM) للنسخ");
      return;
    }
    const confirm = window.confirm(
      `هل تريد نسخ ${nonCustomOrders.length} طلب(ات) غير مخصصة مرة واحدة إلى الإكسيل؟\n\n(الطلبات التي تحتوي على تصميم CUSTOM سيتم تخطيها)\n\nسيتم فحص التكرار + زيادة الرقم المرجع تلقائيًا`
    );
    if (!confirm) return;

    let finalLines = [];
    let hasDuplicate = false;
    let currentRef = currentReference;

    for (const order of nonCustomOrders) {
      try {
        const count = await checkDuplicatePhoneCount(
          order.orderNumber, order.phoneNumber, order.phoneNumber2 || ""
        );
        if (count > 0) {
          hasDuplicate = true;
          finalLines.push(`# تحذير: تكرار رقم (${count} طلب سابق) - ${order.name} - ${order.phoneNumber}`);
        }
        const phone = `${order.phoneNumber}${
          order.phoneNumber2 && order.phoneNumber2 !== "Unknown Phone" ? "-" + order.phoneNumber2 : ""
        }`;
        const formattedState = `${order.state} (D)`;
        const customerBase = `${currentRef}\t\t${order.name}\t${phone}\t\t${order.address}\t${formattedState}\t${order.total}\tCOD\t`;
        const items = order.items;
        const orderLines = [];
        items.forEach((item, index) => {
          const itemNum = `${index + 1}-${items.length}`;
          const itemData = teePlusPackedItemData(item);
          const itemLine = `${itemNum}${itemData}`;
          if (index === 0) {
            orderLines.push(`${customerBase}\t\t${itemLine}`);
          } else {
            orderLines.push(`${currentRef}\t\t\t\t\t\t\t\t\t\t\t${itemLine}`);
          }
        });
        finalLines.push(...orderLines);
        const match = currentRef.match(/(\d+)$/);
        if (match) {
          const num = parseInt(match[0], 10);
          currentRef = currentRef.replace(/\d+$/, (num + 1).toString());
        }
      } catch (err) {
        finalLines.push(`# خطأ في الطلب رقم ${order.orderNumber}`);
      }
    }

    setCurrentReference(currentRef);
    setReferenceInput(currentRef);
    localStorage.setItem("orderReference", currentRef);
    try {
      await copyOrderToTeePlus(finalLines.join("\n"), nonCustomOrders.map((o) => o.orderNumber));
      toast.success(
        `تم نسخ ${nonCustomOrders.length} طلب(ات) غير مخصصة بنجاح!${hasDuplicate ? " (يوجد تكرار في بعض الأرقام)" : ""}`,
        { autoClose: 6000 }
      );
    } catch (err) {
      if (err.code === "SOURCE_FAILED") toast.warn("تم النسخ إلى TeePlus، لكن تعذر تحديث مصدر الإنتاج");
      else toast.error("فشل في النسخ، حاول مرة تانية");
    }
  };

  // ─── Filtered orders ───────────────────────────────────────────────────────

  const filteredOrders = loadedOrders.filter((order) => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return (
      order.orderNumber.toString().includes(term) ||
      order.name.toLowerCase().includes(term) ||
      order.phoneNumber.includes(term) ||
      order.phoneNumber2?.includes(term)
    );
  });

  // ─── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50 to-yellow-50 p-4 md:p-8" dir="rtl">
      {changesDialog}
      <ToastContainer position="top-center" />

      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-8">
          <div className="flex items-center gap-3">
            <Clock className="text-orange-600" size={32} />
            <h1 className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-orange-600 to-amber-600 bg-clip-text text-transparent">
              الطلبات بانتظار التأكيد
            </h1>
          </div>
          <button
            onClick={() => navigate("/dashboard")}
            className="flex items-center gap-2 bg-white/80 backdrop-blur-sm text-gray-700 px-6 py-3 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200"
          >
            <ChevronRight size={20} />
            العودة للرئيسية
          </button>
        </div>

        {/* Quick Message Buttons */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          {/* Stock Apology */}
          <div className="bg-gradient-to-r from-orange-50 to-amber-50 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-orange-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-orange-100 rounded-xl">
                  <MessageSquare className="text-orange-600" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-orange-900 text-lg">رسالة اعتذار (نقص المخزون)</h3>
                  <p className="text-sm text-orange-700">أرسلها للعميل عند التأخير</p>
                </div>
              </div>
              <button
                onClick={handleCopyStockApology}
                className="bg-orange-600 hover:bg-orange-700 text-white px-6 py-3 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 flex items-center gap-2"
              >
                <Copy size={18} />
                نسخ الرسالة
              </button>
            </div>
          </div>

          {/* Placeholder second card */}
          <div className="bg-gradient-to-r from-blue-50 to-cyan-50 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-blue-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-blue-100 rounded-xl">
                  <Truck className="text-blue-600" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-blue-900 text-lg">رسالة بوسطة</h3>
                  <p className="text-sm text-blue-700">لشركة الشحن</p>
                </div>
              </div>
              <button
                onClick={() => {
                  const message = `\nبنحب نبلغ حضرتك إن شركة الشحن هتتواصل مع حضرتك على الواتساب خلال وقت قصير لتأكيد العنوان واستلام الطلب ✅\nتنويه مهم:\nفي رسالة التأكيد اللي هتوصلك ممكن تلاقي اسم شركة مختلفة (مثل TeePlus) 💼  \n\nكمان عدد القطع ممكن يظهر 1 حتى لو كانت اكتر من قطعة واحدة 🧾\nيُرجى الرد على رسالة شركة الشحن علشان يتم تأكيد الطلب ويتم بدء الشحن ✉️⚡  \n\nشكرًا لاختيارك جرابتكم 🌷`;
                  copyToClipboard(message);
                  toast.success("تم نسخ رسالة بوسطة!");
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-xl shadow-md hover:shadow-xl transition-all duration-300 flex items-center gap-2"
              >
                <Copy size={18} />
                نسخ الرسالة
              </button>
            </div>
          </div>
        </div>

        {/* Bulk Actions */}
        <div className="grid grid-cols-1 md:grid-cols-1 gap-4 mb-8">
          <div className="bg-gradient-to-r from-green-50 to-emerald-50 backdrop-blur-sm rounded-2xl shadow-xl p-6 border-2 border-green-300">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-3 bg-green-100 rounded-xl">
                  <CopyCheck className="text-green-600" size={24} />
                </div>
                <div>
                  <h3 className="font-bold text-green-900 text-xl">نسخ جماعي للإكسيل</h3>
                  <p className="text-sm text-green-700">
                    نسخ {loadedOrders.length} طلب مع فحص التكرار
                  </p>
                </div>
              </div>
              <button
                onClick={handleCopyAllVisibleOrders}
                disabled={isOrdersLoading || loadedOrders.length === 0}
                className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-bold px-8 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Copy size={20} />
                نسخ الكل
              </button>
            </div>
          </div>
        </div>

        {/* Reference Section */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 mb-8 border border-white/30">
          <div className="flex items-center gap-3 mb-4">
            <Hash className="text-orange-600" size={24} />
            <h2 className="text-xl font-bold text-gray-800">إعداد رقم المرجع (Reference)</h2>
          </div>
          <div className="flex flex-col md:flex-row items-center gap-4">
            <div className="flex-1 w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1 text-right">الرقم الحالي</label>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const match = currentReference.match(/(\d+)$/);
                    if (match) {
                      const newRef = currentReference.replace(/\d+$/, (parseInt(match[0], 10) + 1).toString());
                      setCurrentReference(newRef);
                      setReferenceInput(newRef);
                      localStorage.setItem("orderReference", newRef);
                    }
                  }}
                  className="bg-green-500 hover:bg-green-600 text-white w-12 h-12 rounded-r-xl flex items-center justify-center transition-all duration-300 hover:scale-110"
                  title="زيادة الرقم"
                >
                  <Plus size={20} />
                </button>
                <div className="flex-1 text-3xl font-bold text-orange-600 bg-orange-50 py-3 px-4 text-center border-2 border-orange-200">
                  {currentReference}
                </div>
                <button
                  onClick={() => {
                    const match = currentReference.match(/(\d+)$/);
                    if (match) {
                      const newNum = parseInt(match[0], 10) - 1;
                      if (newNum >= 0) {
                        const newRef = currentReference.replace(/\d+$/, newNum.toString());
                        setCurrentReference(newRef);
                        setReferenceInput(newRef);
                        localStorage.setItem("orderReference", newRef);
                      }
                    }
                  }}
                  className="bg-red-500 hover:bg-red-600 text-white w-12 h-12 rounded-l-xl flex items-center justify-center transition-all duration-300 hover:scale-110"
                  title="إنقاص الرقم"
                >
                  <Minus size={20} />
                </button>
              </div>
            </div>
            <div className="flex-1 w-full">
              <label className="block text-sm font-medium text-gray-700 mb-1 text-right">
                ابدأ من (مثال: Girabatcom.150)
              </label>
              <input
                type="text"
                value={referenceInput}
                onChange={(e) => setReferenceInput(e.target.value)}
                onBlur={() => {
                  if (referenceInput.trim() && referenceInput.includes(".")) {
                    setCurrentReference(referenceInput.trim());
                    localStorage.setItem("orderReference", referenceInput.trim());
                    toast.success("تم تحديث رقم المرجع!");
                  }
                }}
                className="w-full p-4 text-lg border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-orange-300 focus:border-orange-400 transition-all"
                placeholder="Girabatcom.4000"
              />
            </div>
            <button
              onClick={() => {
                if (referenceInput.trim()) {
                  setCurrentReference(referenceInput.trim());
                  setReferenceInput(referenceInput.trim());
                  localStorage.setItem("orderReference", referenceInput.trim());
                  toast.success("تم تحديث رقم المرجع!");
                }
              }}
              className="bg-gradient-to-r from-orange-600 to-amber-600 text-white px-8 py-4 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-bold flex items-center gap-2"
            >
              <Save size={20} />
              تحديث الرقم
            </button>
            <button
              onClick={handleCopyAllVisibleOrders}
              disabled={isOrdersLoading || loadedOrders.length === 0}
              className="bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white font-bold px-5 py-4 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 disabled:cursor-not-allowed flex items-center gap-2"
            >
              <Copy size={20} />
              نسخ الكل ({loadedOrders.length})
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-3 text-right">
            كل ما تنسخ بيانات عميل، الرقم سيزيد تلقائيًا
          </p>
        </div>

        {/* Search Bar */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-4 mb-6 border border-white/30">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="بحث برقم الطلب أو اسم العميل أو رقم الهاتف..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pr-12 pl-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-orange-300 focus:border-orange-400 transition-all"
              />
            </div>
          </div>
        </div>

        {/* Page size selector */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-4 mb-6 border border-white/30">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <label className="font-medium text-gray-700">عرض:</label>
              <select
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                className="px-4 py-2 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-4 focus:ring-orange-300 focus:border-orange-400 transition-all bg-white"
                disabled={isOrdersLoading}
              >
                <option value={10}>10 طلبات</option>
                <option value={20}>20 طلب</option>
                <option value={30}>30 طلب</option>
                <option value={40}>40 طلب</option>
                <option value={50}>50 طلب</option>
                <option value={80}>80 طلب</option>
                <option value={100}>100 طلب</option>
              </select>
              <span className="text-2xl text-gray-600">
                (إجمالي المعروض: {loadedOrders.length})
              </span>
            </div>
            {isOrdersLoading && (
              <div className="flex items-center gap-2 text-orange-600">
                <Loader className="animate-spin" size={20} />
                <span>جاري التحميل...</span>
              </div>
            )}
          </div>
        </div>

        {/* Orders Table */}
        <div className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 border border-white/30 overflow-x-auto">
          {isOrdersLoading ? (
            <SkeletonLoader type="table" />
          ) : (
            <>
              {filteredOrders.length > 0 ? (
                <>
                  <table className="w-full min-w-[1200px] table-auto">
                    <thead>
                      <tr className="bg-gradient-to-r from-orange-50 to-amber-50 border-b-2 border-orange-200">
                        <th className="p-4 text-right font-bold text-orange-800">رقم الطلب</th>
                        <th className="p-4 text-right font-bold text-orange-800">العميل</th>
                        <th className="p-4 text-right font-bold text-orange-800">العنوان</th>
                        <th className="p-4 text-center font-bold text-orange-800">الإجمالي</th>
                        <th className="p-4 text-right font-bold text-orange-800">المنتجات + الرابط</th>
                        <th className="p-4 text-right font-bold text-orange-800">ملاحظات</th>
                        <th className="p-4 text-center font-bold text-orange-800">نسخ Excel</th>
                        <th className="p-4 text-center font-bold text-orange-800">تأكيد / إلغاء</th>
                        <th className="p-4 text-center font-bold text-orange-800">إجراءات</th>
                        <th className="p-4 text-center font-bold text-orange-800">حذف</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredOrders.map((order) => (
                        <tr key={order.orderNumber} className="border-b border-gray-200 hover:bg-white/60 transition-colors">
                          {/* Order Number */}
                          <td className="p-4">
                            <span className="font-mono font-bold text-orange-600">#{order.orderNumber}</span>
                          </td>

                          {/* Customer */}
                          <td className="p-4">
                            <div className="flex items-center gap-2">
                              <User size={16} className="text-gray-500" />
                              <span style={{ fontFamily: "cairo black" }} className="text-lg">{order.name}</span>
                            </div>
                            <br />
                            <div className="space-y-1">
                              <a
                                href={`https://wa.me/${order.phoneNumber.replace(/[^0-9]/g, "").replace(/^0/, "20")}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-1 text-green-600 hover:text-green-800 transition"
                              >
                                <Phone size={14} />
                                {order.phoneNumber}
                              </a>
                              {order.phoneNumber2 && order.phoneNumber2 !== "غير معروف" && order.phoneNumber2 !== "Unknown Phone" && (
                                <a
                                  href={`https://wa.me/${order.phoneNumber2.replace(/[^0-9]/g, "").replace(/^0/, "20")}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="flex items-center gap-1 text-green-600 hover:text-green-800 text-sm transition"
                                >
                                  <Phone size={12} />
                                  {order.phoneNumber2}
                                </a>
                              )}
                            </div>
                          </td>

                          {/* Address */}
                          <td className="p-4 max-w-[200px]">
                            <div className="flex items-start gap-2">
                              <MapPin size={16} className="text-gray-500 mt-1 flex-shrink-0" />
                              <span style={{ fontFamily: "cairo black" }} className="text-[13px] line-clamp-5">{order.address}</span>
                            </div>
                            <br />
                            <div className="flex items-center gap-2">
                              <Map size={16} className="text-gray-500" />
                              <span className="text-2xl text-purple-600">{order.state}</span>
                            </div>
                          </td>

                          {/* Total */}
                          <td className="p-4 text-center">
                            <span className="font-bold text-xl text-green-600">{order.total} ج.م</span>
                          </td>

                          {/* Products + Drive Links */}
                          <td className="p-4">
                            {order.items.map((item, index) => (
                              <div key={`${item.id}-${index}`} className="mb-3">
                                <div className="flex items-start gap-2">
                                  <div className="flex-1">
                                    <div className="font-medium text-orange-700">{item.name}</div>
                                    <div style={{ fontFamily: "cairo black" }} className="text-[13px] text-black-600">{item.type}</div>
                                    <div className="flex items-center gap-2 mt-1">
                                      <span className="text-xs bg-gray-100 px-2 py-1 rounded font-mono">{item.code}</span>
                                      {item.driveLink && (
                                        <div className="flex items-center gap-1">
                                          <a
                                            href={item.driveLink}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-orange-600 hover:text-orange-800 transition-transform hover:scale-110"
                                            title="فتح الرابط"
                                          >
                                            <LinkIcon size={16} />
                                          </a>
                                          {item.code.startsWith("CUSTOM") && (
                                            <button
                                              onClick={() => handleDownload(item.driveLink, `${order.orderNumber}-${item.id}.jpg`)}
                                              className="text-green-600 hover:text-green-800 transition-transform hover:scale-110"
                                              title="تحميل الصورة"
                                            >
                                              <Download size={16} />
                                            </button>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                </div>
                                {index < order.items.length - 1 && <hr className="my-2 border-gray-200" />}
                              </div>
                            ))}
                          </td>

                          {/* Notes */}
                          <td className="p-2 max-w-[150px]">
                            <p style={{ fontFamily: "cairo black" }} className="text-lg text-gray-600 line-clamp-3">{order.note || "—"}</p>
                          </td>

                          {/* Copy Excel */}
                          <td className="p-4">
                            {isAdmin(authState.role) && (
                              <button
                                onClick={() => handleCopyAllExcelData(order)}
                                className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white px-3 py-4 rounded-xl hover:shadow-lg hover:scale-105 transition-all duration-300 text-sm font-bold flex items-center justify-center gap-2"
                              >
                                <Copy size={16} />
                                نسخ (TeePlus)
                              </button>
                            )}
                          </td>

                          {/* Confirm / Cancel */}
                          <td className="p-4">
                            <div className="flex flex-col gap-2 min-w-[110px]">
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Confirming")}
                                className="bg-green-600 text-white px-3 py-3 rounded-xl hover:bg-green-700 text-sm font-bold transition-all flex items-center justify-center gap-1"
                              >
                                <CheckCircle size={14} />
                                وافق ينفذ
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Canceled")}
                                className="bg-red-500 text-white px-3 py-3 rounded-xl hover:bg-red-600 text-sm font-bold transition-all flex items-center justify-center gap-1"
                              >
                                <XCircle size={14} />
                                لم يوافق هيكنسل
                              </button>
                            </div>
                          </td>

                          {/* Actions */}
                          <td className="p-4">
                            <div className="flex flex-col gap-2 min-w-[100px]">
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Hold")}
                                className="bg-yellow-500 text-white px-3 py-2 rounded-lg hover:bg-yellow-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Clock size={14} />
                                  معلق
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Changes")}
                                className="bg-purple-500 text-white px-3 py-2 rounded-lg hover:bg-purple-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Edit size={14} />
                                  تغييرات
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Under Purchase")}
                                className="bg-indigo-500 text-white px-3 py-2 rounded-lg hover:bg-indigo-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <ShoppingCart size={14} />
                                  تحت الشراء
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Deposit Required")}
                                className="bg-orange-500 text-white px-3 py-2 rounded-lg hover:bg-orange-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <DollarSign size={14} />
                                  ديبوزت
                                </div>
                              </button>
                            </div>
                          </td>

                          {/* Delete */}
                          <td className="p-4">
                            <div className="flex flex-col gap-2 min-w-[80px]">
                              <button
                                onClick={() => handleDeleteOrder(order.orderNumber)}
                                className="bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <Trash2 size={14} />
                                  حذف
                                </div>
                              </button>
                              <button
                                onClick={() => handleUpdateStatus(order.orderNumber, "Spam")}
                                className="bg-red-500 text-white px-3 py-2 rounded-lg hover:bg-red-600 text-sm font-medium transition-all"
                              >
                                <div className="flex items-center justify-center gap-1">
                                  <AlertTriangle size={14} />
                                  Spam
                                </div>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {/* Load More */}
                  {hasMore && (
                    <div className="text-center mt-8">
                      <button
                        onClick={loadMoreOrders}
                        disabled={isLoadingMore}
                        className="bg-gradient-to-r from-orange-600 to-amber-600 text-white px-8 py-4 rounded-xl hover:shadow-xl hover:scale-105 transition-all duration-300 font-bold text-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 mx-auto"
                      >
                        {isLoadingMore ? (
                          <>
                            <Loader className="animate-spin" size={20} />
                            جاري التحميل...
                          </>
                        ) : (
                          <>
                            <ChevronLeft size={20} />
                            تحميل المزيد
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="text-center py-16">
                  <Clock className="mx-auto mb-4 text-gray-400" size={64} />
                  <p className="text-2xl text-gray-700 mb-2">لا توجد طلبات بانتظار التأكيد</p>
                  <p className="text-lg text-gray-500">سيتم عرض الطلبات هنا عند وجودها</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Scroll to top */}
        <div className="fixed bottom-6 right-6">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="bg-gradient-to-r from-orange-600 to-amber-600 text-white p-4 rounded-full shadow-2xl hover:shadow-xl hover:scale-110 transition-all duration-300 flex items-center justify-center"
            title="العودة إلى الأعلى"
          >
            <ChevronUp size={24} />
          </button>
        </div>
      </div>
    </div>
  );
};

export default WaitingConfirmingOrders;